import type { ClientFrame, ServerFrame, WsConnectionState } from '@talkvibe/shared';

import { chatService } from './chat.ts';
import { ReconnectingSocket } from './reconnecting-socket.ts';

/**
 * WebSocket client for one conversation.
 *
 * Open only while a chat is on screen; it carries the messages, typing
 * indicators and read receipts of that conversation and nothing else. Anything
 * that must reach the user whether or not a chat is open goes over the user
 * channel instead — see `user-channel.ts`.
 *
 * The connection lifecycle itself lives in `ReconnectingSocket`.
 */
export interface RealtimeHandlers {
  onFrame: (frame: ServerFrame) => void;
  onStateChange: (state: WsConnectionState) => void;
}

export class RealtimeConnection extends ReconnectingSocket<ClientFrame> {
  constructor(
    private readonly conversationId: string,
    private readonly handlers: RealtimeHandlers,
  ) {
    super(handlers.onStateChange);
  }

  protected async ticketUrl(): Promise<string> {
    const ticket = await chatService.realtimeTicket(this.conversationId);
    return ticket.url;
  }

  protected handleFrame(frame: unknown): void {
    const typed = frame as ServerFrame;
    if (typeof typed?.type !== 'string') return;
    // Heartbeat replies are plumbing; the rest of the app should not see them.
    if (typed.type === 'pong') return;
    this.handlers.onFrame(typed);
  }

  protected heartbeat(): ClientFrame {
    return { type: 'ping', conversationId: this.conversationId, payload: {} };
  }

  /** A typing indicator that arrives after the outage is worse than none. */
  protected override shouldQueue(frame: ClientFrame): boolean {
    return frame.type !== 'typing';
  }
}
